import { useNavigate } from 'react-router-dom'
import { F0AvatarPerson, F0Box, F0Dialog, F0Link, F0TagStatus, F0Text } from '@factorialco/f0-react'

import { ExpenseBudgetMovementFragment } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { expenseStatusVariant, expenseStatusLabel } from 'modules/finance/lib/budgetMovement'

interface Props {
  item: ExpenseBudgetMovementFragment
  onClose: () => void
  locale: string
}

export const RecordDetail = ({ item, onClose, locale }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()

  const employee = item.employee
  const expenseUrl = item.resourceId ? navigation.helpers.expensableDetail(item.resourceId) : null

  // Get category name from expensable
  const getCategoryName = (): string => {
    const expensable = item.expensable
    if (!expensable) return '-'
    return (
      expensable.expense?.category?.name ??
      expensable.mileage?.category?.name ??
      expensable.perDiem?.category?.name ??
      '-'
    )
  }

  const status = item.expensable?.status
  const categoryName = getCategoryName()
  const documentDate = item.effectiveAt ? dateBackendDateTimeToUI(item.effectiveAt) : null
  const amount = item.amountCents
    ? amountWithCurrency(Number(item.amountCents), item.targetCurrency, locale)
    : null

  const handleGoToExpense = () => {
    if (expenseUrl) {
      onClose()
      navigate(expenseUrl)
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      position="right"
      title={i18n.t('budgets.expense_budget_detail.records.sidepanel.title')}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        {/* Employee */}
        {employee && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text variant="label" content={i18n.t('common.employee')} />
            <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
              <F0AvatarPerson
                size="md"
                src={buildUrl(employee.avatar?.url)}
                firstName={employee.fullName.split(' ')[0] || ''}
                lastName={employee.fullName.split(' ').slice(1).join(' ') || ''}
              />
              <F0Text variant="body" content={employee.fullName} />
            </F0Box>
          </F0Box>
        )}

        {/* Legal Entity */}
        {employee?.legalEntity?.legalName && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('budgets.detail.tabs.employees.detail.legal_entity')}
            />
            <F0Text variant="body" content={employee.legalEntity.legalName} />
          </F0Box>
        )}

        {/* Amount */}
        {amount && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('budgets.detail.tabs.spendings.table.amount')}
            />
            <F0Text variant="body" content={amount} />
          </F0Box>
        )}

        {/* Category */}
        {categoryName !== '-' && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('budgets.detail.tabs.spendings.table.category')}
            />
            <F0Text variant="body" content={categoryName} />
          </F0Box>
        )}

        {/* Status */}
        {status && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('budgets.detail.tabs.spendings.table.status')}
            />
            <F0TagStatus text={expenseStatusLabel(status)} variant={expenseStatusVariant(status)} />
          </F0Box>
        )}

        {/* Document Date */}
        {documentDate && (
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Text
              variant="label"
              content={i18n.t('budgets.detail.tabs.spendings.table.document_date')}
            />
            <F0Text variant="body" content={documentDate} />
          </F0Box>
        )}

        {/* Source */}
        {expenseUrl && (
          <F0Box display="flex" flexDirection="column" gap="sm" alignItems="start">
            <F0Text
              variant="label"
              content={i18n.t('budgets.expense_budget_detail.records.sidepanel.source')}
            />
            <F0Link onClick={handleGoToExpense}>
              {i18n.t('budgets.expense_budget_detail.records.sidepanel.link_to_expense')}
            </F0Link>
          </F0Box>
        )}
      </F0Box>
    </F0Dialog>
  )
}
